import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { BaseModal } from 'src/design/molecules/BaseModal';
import { MRT_ColumnDef } from 'mantine-react-table';
import React from 'react';
import { JobInvoiceDataDto } from 'src/api/FiretrolApiClient';
import { formatCurrency } from 'src/design/atoms/Currency';
import { useTableExports } from 'src/design/organisms/useTableExports';
import { formatDate } from 'src/utils';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';

interface InvoicesDialogProps {
  handleClose: () => void;
  open: boolean;
  invoices: JobInvoiceDataDto[];
}

export const InvoicesDialog: React.FC<InvoicesDialogProps> = ({
  handleClose,
  open,
  invoices,
}) => {
  const columns = React.useMemo<MRT_ColumnDef<JobInvoiceDataDto>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 80,
      },
      {
        accessorKey: 'custname',
        header: 'Customer Name',
        size: 200,
      },
      {
        accessorKey: 'docType',
        header: 'Doc Type',
        size: 150,
      },
      {
        accessorKey: 'documentNumber',
        header: 'Doc Number',
        size: 150,
      },
      {
        id: 'docDate',
        accessorFn: (row) => formatDate(row.docDate),
        header: 'Date',
        size: 120,
      },
      {
        id: 'documentAmount',
        accessorFn: (row) =>
          formatCurrency(row.documentAmount || 0, { precise: true }),
        header: 'Doc Amount',
        size: 150,
      },
      {
        id: 'contractBilled',
        accessorFn: (row) =>
          formatCurrency(row.contractBilled || 0, { precise: true }),
        header: 'Contract Billed',
        size: 150,
      },
      {
        id: 'cashReceived',
        accessorFn: (row) =>
          formatCurrency(row.cashReceived || 0, { precise: true }),
        header: 'Cash Received',
        size: 150,
      },
      {
        id: 'cashRemainingAmount',
        accessorFn: (row) =>
          formatCurrency(row.cashRemainingAmount || 0, { precise: true }),
        header: 'Remaining Amount',
        size: 150,
      },
    ],
    [],
  );

  const tableState = useTableState({
    identifier: 'ctc-invoices-dialog-table',
    columns: columns,
    defaultSorting: [{ id: 'docDate', desc: true }],
  });

  const tableExports = useTableExports<JobInvoiceDataDto>(
    'ctc-invoices',
    (data) => {
      return data.map((item) => ({
        ['ID']: item.id,
        ['Customer Name']: item.custname,
        ['Doc Type']: item.docType,
        ['Doc Number']: item.documentNumber,
        ['Date']: formatDate(item.docDate),
        ['Doc Amount']: item.documentAmount || 0,
        ['Contract Billed']: item.contractBilled || 0,
        ['Cash Received']: item.cashReceived || 0,
        ['Remaining Amount']: item.cashRemainingAmount || 0,
      }));
    },
  );

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: false,
      enableClickToCopy: false,
      columns: columns,
      data: invoices,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
    },
    getRowId: (row) => String(row.id),
    totalCount: invoices.length,
    isLoading: false,
    isFetching: false,
    isError: false,
    ...tableExports,
  });

  return (
    <BaseModal
      opened={open}
      onClose={handleClose}
      title={<Title order={3}>Invoices</Title>}
      size="xl"
    >
      {invoices.length > 0 ? (
        <>{table}</>
      ) : (
        <Text c="dimmed" ta="center" py="xl">
          No invoices found
        </Text>
      )}
    </BaseModal>
  );
};
